import { useCallback, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AssistantUiAction } from '@/api/assistant/assistantApi.types';
import { ASSISTANT_TARGET_WAIT_MILLISECONDS } from '@/constants/assistant';
import { prefersReducedMotion } from '../mediaQueries';
import { buildAssistantDestinationPath } from './assistantDestinations';
import {
  canNavigateAutomatically,
  isWorkAtRisk,
  leavesCurrentScreen,
  type AssistantPageState,
} from './assistantNavigationGuard';
import type { SessionNoteFormRegistry } from '../sessionNote/sessionNoteFormRegistry';
import { useAssistantUi } from './assistantUiContext';

const TARGET_MISSING_LOG_LABEL = 'assistant_ui_target_missing';

export type AssistantUiActionOutcome =
  'performed' | 'needs_confirmation' | 'unsaved_changes' | 'target_missing' | 'unavailable';

export interface AssistantUiActionRequest {
  isRequestedByUser: boolean;
  allowLeavingUnsavedWork: boolean;
}

export const AUTOMATIC_ACTION_REQUEST: AssistantUiActionRequest = {
  isRequestedByUser: false,
  allowLeavingUnsavedWork: false,
};

function openFormUnsavedChanges(forms: SessionNoteFormRegistry): boolean | null {
  const form = forms.current();
  return form === null ? null : form.hasUnsavedChanges();
}

export function useAssistantUiActionExecutor() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const { registry, showSpotlight, announce, hasEditsOnCurrentPage, sessionNoteForms } =
    useAssistantUi();
  const { t } = useTranslation('assistant');

  useLayoutEffect(function keepLatestLocation() {
    locationRef.current = location;
  });

  return useCallback(
    async function executeAction(
      action: AssistantUiAction,
      request: AssistantUiActionRequest
    ): Promise<AssistantUiActionOutcome> {
      const path = buildAssistantDestinationPath(action);
      if (path === null) {
        return 'unavailable';
      }
      const currentLocation = locationRef.current;
      const page: AssistantPageState = {
        pathname: currentLocation.pathname,
        search: currentLocation.search,
        hasEdits: hasEditsOnCurrentPage(),
        openFormHasUnsavedChanges: openFormUnsavedChanges(sessionNoteForms),
      };
      if (request.isRequestedByUser === false) {
        if (canNavigateAutomatically(page, path, document) === false) {
          return 'needs_confirmation';
        }
      } else if (
        request.allowLeavingUnsavedWork === false &&
        leavesCurrentScreen(page, path) &&
        isWorkAtRisk(page, document)
      ) {
        return 'unsaved_changes';
      }
      const announcements: string[] = [];
      if (leavesCurrentScreen(page, path)) {
        navigate(path);
        announcements.push(t('announcements.moved'));
      }
      if (action.kind !== 'highlight') {
        announce(announcements.join(' '));
        return 'performed';
      }
      const foundElements = await Promise.all(
        action.targets.map(function waitForTarget(target) {
          return registry.waitFor(
            target.target,
            target.entityId,
            ASSISTANT_TARGET_WAIT_MILLISECONDS
          );
        })
      );
      const elements = foundElements.filter(function isElement(element): element is HTMLElement {
        return element !== null && element.isConnected;
      });
      const firstElement = elements[0];
      if (firstElement === undefined) {
        console.warn(
          TARGET_MISSING_LOG_LABEL,
          action.targets.map(function readTargetKey(target) {
            return target.target;
          })
        );
        announcements.push(t('announcements.targetMissing'));
        announce(announcements.join(' '));
        return 'target_missing';
      }
      firstElement.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'center',
      });
      showSpotlight({ elements, caption: action.caption });
      announcements.push(t('announcements.highlighted'));
      announce(announcements.join(' '));
      return 'performed';
    },
    [announce, hasEditsOnCurrentPage, navigate, registry, sessionNoteForms, showSpotlight, t]
  );
}
